/**
 * T1040cr · Statement Review & Rate Analyzer
 *
 * Mocked merchant-services statement parser.  The operator drops any
 * file (or clicks "Load sample") and we show a deterministic parsed
 * breakdown — effective rate, interchange / dues / assessments, and a
 * "Current vs Proposed vs Savings" summary that drives the pitch.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import ActiveCardShell from "./ActiveCardShell";
import { useDemoBrand } from "@/lib/demoBrand";
import { Receipt, UploadCloud, FileCheck2, TrendingDown } from "lucide-react";

const SAMPLE = {
  merchant:         "Birchwood Grill & Tap",
  statement_month:  "January 2026",
  processor:        "Fiserv / First Data",
  gross_volume:     142380.42,
  net_volume:       138902.11,
  tx_count:         3184,
  avg_ticket:       44.72,
  lines: [
    { k: "Interchange (visa/mc)",     v: 2210.44, pct: 1.55 },
    { k: "Dues & assessments",        v:  192.11, pct: 0.13 },
    { k: "Processor markup (bps 65)", v:  925.47, pct: 0.65 },
    { k: "Monthly / batch fees",      v:   89.00, pct: 0.06 },
    { k: "PCI / statement fees",      v:   29.95, pct: 0.02 },
    { k: "Non-qualified surcharge",   v:  187.60, pct: 0.13 },
  ],
  proposed_bps:  25,   // VEROOGLE markup
};

export default function StatementAnalyzerCard() {
  const { name } = useDemoBrand();
  const [parsed, setParsed] = useState(null);
  const [parsing, setParsing] = useState(false);

  const parse = async () => {
    setParsing(true);
    await new Promise((r) => setTimeout(r, 900));
    setParsed(SAMPLE);
    setParsing(false);
    toast.success("Statement parsed · 7 line items extracted");
  };

  const onDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) toast.success(`Received ${file.name} · routing to parser`);
    await parse();
  };

  const total = parsed ? parsed.lines.reduce((a, b) => a + b.v, 0) : 0;
  const currentEff = parsed ? (total / parsed.gross_volume) * 100 : 0;
  const proposedTotal = parsed
    ? parsed.lines.filter((l) => !l.k.includes("markup")).reduce((a, b) => a + b.v, 0)
      + (parsed.gross_volume * (SAMPLE.proposed_bps / 10000))
    : 0;
  const proposedEff = parsed ? (proposedTotal / parsed.gross_volume) * 100 : 0;
  const monthlySavings = parsed ? total - proposedTotal : 0;
  const annualSavings = monthlySavings * 12;

  return (
    <ActiveCardShell
      id="T1040cr"
      kicker="RATE ANALYZER"
      title="Statement Review & Rate Analyzer"
      Icon={Receipt}
      actions={
        <button
          data-testid="veroogle-t1040-sample"
          onClick={parse}
          disabled={parsing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono tracking-[0.14em] uppercase bg-brand-cyan/90 hover:bg-brand-cyan text-navy-950 font-semibold transition disabled:opacity-60"
        >
          <FileCheck2 className="w-3.5 h-3.5" /> {parsing ? "Parsing…" : "Load sample"}
        </button>
      }
    >
      {/* Drop zone */}
      <label
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        htmlFor="veroogle-t1040-file"
        data-testid="veroogle-t1040-drop"
        className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-white/15 hover:border-brand-cyan/60 bg-white/[0.02] px-6 py-8 text-center transition cursor-pointer"
      >
        <UploadCloud className="w-6 h-6 text-slate-400" />
        <div className="text-slate-300 text-sm">Drop a merchant statement PDF — or click to pick a file</div>
        <div className="text-slate-500 font-mono text-[10px] tracking-[0.18em] uppercase">Sandboxed · nothing is uploaded</div>
        <input
          id="veroogle-t1040-file"
          type="file"
          className="hidden"
          onChange={() => parse()}
        />
      </label>

      {parsed && (
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-5">
          {/* Line items */}
          <div className="rounded-xl border border-white/8 overflow-hidden">
            <div className="px-4 py-2 bg-white/[0.03] border-b border-white/8 flex items-center justify-between flex-wrap gap-2">
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">
                {parsed.merchant} · {parsed.statement_month} · {parsed.processor}
              </div>
              <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-brand-cyan">
                {parsed.tx_count.toLocaleString()} tx · avg ${parsed.avg_ticket.toFixed(2)}
              </div>
            </div>
            <table className="w-full text-sm" data-testid="veroogle-t1040-lines">
              <thead>
                <tr className="text-left font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 border-b border-white/8">
                  <th className="px-4 py-2 font-normal">Line item</th>
                  <th className="px-4 py-2 font-normal text-right">% of gross</th>
                  <th className="px-4 py-2 font-normal text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {parsed.lines.map((l, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="px-4 py-2.5 text-slate-200">{l.k}</td>
                    <td className="px-4 py-2.5 text-right text-slate-400 font-mono">{l.pct.toFixed(2)}%</td>
                    <td className="px-4 py-2.5 text-right text-slate-100 font-mono">${l.v.toLocaleString(undefined,{minimumFractionDigits:2})}</td>
                  </tr>
                ))}
                <tr className="bg-white/[0.03]">
                  <td className="px-4 py-2.5 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">Total fees</td>
                  <td className="px-4 py-2.5 text-right text-brand-cyan font-mono">{currentEff.toFixed(2)}%</td>
                  <td className="px-4 py-2.5 text-right text-brand-cyan font-mono">${total.toLocaleString(undefined,{minimumFractionDigits:2})}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Savings panel */}
          <aside className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex flex-col gap-3" data-testid="veroogle-t1040-savings">
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-emerald-300">// {name} buy-rate · {SAMPLE.proposed_bps} bps markup</div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400">Current effective</div>
              <div className="font-mono text-2xl text-rose-300">{currentEff.toFixed(2)}%</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400">Proposed effective</div>
              <div className="font-mono text-2xl text-emerald-300">{proposedEff.toFixed(2)}%</div>
            </div>
            <div className="pt-3 border-t border-emerald-500/20">
              <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Monthly savings</div>
              <div className="font-mono text-2xl text-white">${monthlySavings.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400">Annual savings</div>
              <div className="font-mono text-xl text-emerald-200">${annualSavings.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</div>
            </div>
          </aside>
        </div>
      )}
    </ActiveCardShell>
  );
}
